import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import competitionsData from '@/data/competitions.json';
import type { CompetitionEvent } from '@/data/types';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ResultsBoard from './components/ResultsBoard';

const events = competitionsData as CompetitionEvent[];

function formatDate(iso: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
}

export default function EventResultsPage() {
  const { slug } = useParams<{ slug: string }>();
  const event = events.find((e) => e.slug === slug);

  if (!event) {
    return (
      <div className="min-h-screen bg-[#111111] flex flex-col items-center justify-center text-center px-4">
        <p className="text-5xl font-bold text-white mb-4">404</p>
        <p className="text-gray-400 mb-8">Event not found.</p>
        <Link to="/events" className="text-[#14B8A6] hover:underline text-sm">← All events</Link>
      </div>
    );
  }

  const date = formatDate(event.date);
  const teamCount = event.categories.reduce((n, c) => n + c.results.length, 0);

  return (
    <div className="min-h-screen bg-black text-white">
      <Helmet>
        <title>{event.name} — Results & Rankings | HUB30 Jbeil</title>
        <meta
          name="description"
          content={`Full results and rankings for ${event.name} at HUB30 gym in Jbeil — ${teamCount} teams across ${event.categories.length} categories. Search by athlete name.`}
        />
        <link rel="canonical" href={`https://hub30gym.com/events/${event.slug}`} />
        <meta property="og:title" content={`${event.name} — Results & Rankings | HUB30`} />
        <meta property="og:description" content={`Results and rankings for ${event.name} at HUB30, Jbeil.`} />
        <meta property="og:url" content={`https://hub30gym.com/events/${event.slug}`} />
      </Helmet>

      <Navbar />

      {/* Header */}
      <div className="relative overflow-hidden pt-28 pb-10 lg:pt-32 lg:pb-14 px-4">
        <div className="absolute inset-0 bg-gradient-to-b from-[#14B8A6]/8 via-transparent to-black" />
        <div className="max-w-5xl mx-auto relative z-10">
          <Link
            to="/events"
            className="inline-flex items-center gap-2 text-gray-400 hover:text-white text-sm transition-colors mb-6"
          >
            <i className="ri-arrow-left-line" />
            All events
          </Link>
          <span className="inline-block text-xs font-semibold text-[#14B8A6] bg-[#14B8A6]/10 border border-[#14B8A6]/20 px-3 py-1 rounded-full mb-4">
            {event.discipline}
          </span>
          <h1 className="text-3xl sm:text-4xl lg:text-6xl font-bold mb-3">{event.name}</h1>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-gray-400 text-sm">
            {date && (
              <span className="inline-flex items-center gap-2">
                <i className="ri-calendar-line text-[#14B8A6]" />
                {date}
              </span>
            )}
            {event.location && (
              <span className="inline-flex items-center gap-2">
                <i className="ri-map-pin-line text-[#14B8A6]" />
                {event.location}
              </span>
            )}
            <span className="inline-flex items-center gap-2">
              <i className="ri-team-line text-[#14B8A6]" />
              {teamCount} teams
            </span>
          </div>
          {event.description && (
            <p className="text-gray-400 text-base mt-5 max-w-2xl leading-relaxed">{event.description}</p>
          )}
        </div>
      </div>

      {/* Results */}
      <div className="max-w-5xl mx-auto px-4 pb-24">
        <ResultsBoard event={event} />
      </div>

      <Footer />
    </div>
  );
}
